import React, { useCallback, useEffect, useState } from "react";
import { ScreeningResultsControlled, ScreeningResultsControlledProperty } from "./card-controlled";
import {
  BACKGROUND_CHECK_STATUS_ENDPOINT,
  BACKGROUND_CHECK_STATUS_SF_ENDPOINT,
  BACKGROUND_CHECK_STATUS_AIR_ENDPOINT,
  buildUrl,
  DRUG_TEST_STATUS_ENDPOINT,
  RIGHT_TO_WORK_STATUS_ENDPOINT,
  DRUG_TEST_STATUS_GID_ENDPOINT,
} from "../../config/urls";
import { getCandidateData } from "../../utility/candidate-data";
import { getCaasInfo } from "../../service/get-caas-info";
import { fetchWithTimeout } from "../../helpers/fetcher";
import { Stage } from "../../helpers/get-settings";
import { I9Response } from "../i9-task/i9-controlled";
import { getJobScheduleDetails } from "../../utility/application-data";
import {
  getTaskDefinitionsFromConfig,
  isGlobalIdEnabledByAppConfig,
  isTaskEnabledByAppConfig,
} from "../../utility/app-config-helper";
import {
  BGC_CHECK_IN_SCREENING_TASK_ID,
  AIR_BGC_IN_SCREENING_CHECK_TASK_ID,
  DT_IN_SCREENING_CHECK_TASK_ID,
  I9_TASK_ID,
  RIGHT_TO_WORK_TASK_DEFINITION_ID,
  RIGHT_TO_WORK_TASK_TITLE,
  LAUNCHED_COUNTRY,
} from "../../config/appConfigConstants";
import { isSalesforceApplication } from "../../helpers/url-params";
import { getApplicationState, getValidStatesForShowScreeningResult } from "../../helpers/utils";
import { getRegionFromCookie } from "../../utility/locale-helper";

interface ScreeningResultsUncontrolledProps {
  applicationId: string;
  stage?: Stage;
}

export interface ContingencyResponse {
  status?: string;
}

export const ScreeningResultsUncontrolled = ({ applicationId, stage }: ScreeningResultsUncontrolledProps) => {
  const [i9Response, setI9Response] = useState<I9Response>();
  const [bgcResponse, setBgcResponse] = useState<ContingencyResponse>();
  const [dtResponse, setDtResponse] = useState<ContingencyResponse>();
  const [airBgcResponse, setAirBgcResponse] = useState<ContingencyResponse>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<boolean>(false);

  const requestData = useCallback(async () => {
    setLoading(true);

    try {
      let jcDetails;

      const { candidateId, sfCandidateId, candidateGlobalId } = await getCandidateData(applicationId);
      if (!candidateId || !candidateGlobalId) {
        setError(true);
      }

      const validateScreeningResultStates = async () => {
        const { applicationState } = await getApplicationState(applicationId);

        const validStates = getValidStatesForShowScreeningResult(getRegionFromCookie() as LAUNCHED_COUNTRY);
        if (!applicationState) {
          setError(true);
        } else if (!validStates.includes(applicationState)) {
          setError(true);
        }
      };

      if (!isSalesforceApplication(applicationId)) {
        jcDetails = await getJobScheduleDetails(applicationId);
        validateScreeningResultStates();
      }

      const bgcURL = buildUrl(
        isSalesforceApplication(applicationId) ? BACKGROUND_CHECK_STATUS_SF_ENDPOINT : BACKGROUND_CHECK_STATUS_ENDPOINT,
        {
          candidateId: candidateId,
          applicationId,
        }
      );

      const airBgcURL = buildUrl(BACKGROUND_CHECK_STATUS_AIR_ENDPOINT, {
        candidateId: candidateId,
        applicationId,
      });

      const dtURL = isGlobalIdEnabledByAppConfig()
        ? buildUrl(DRUG_TEST_STATUS_GID_ENDPOINT, {
            candidateGlobalId,
            applicationId,
            businessLine: jcDetails?.businessLine,
          })
        : buildUrl(DRUG_TEST_STATUS_ENDPOINT, {
            sfCandidateId,
            applicationId,
            businessLine: jcDetails?.businessLine,
          });

      const rtwTaskDefinitions = getTaskDefinitionsFromConfig(RIGHT_TO_WORK_TASK_DEFINITION_ID, applicationId);

      if (isTaskEnabledByAppConfig(I9_TASK_ID, applicationId) && rtwTaskDefinitions.isIntegratedWithCaaS) {
        if (rtwTaskDefinitions.taskTitle == RIGHT_TO_WORK_TASK_TITLE) {
          setI9Response(await getCaasInfo(candidateId, RIGHT_TO_WORK_STATUS_ENDPOINT));
        } else {
          setI9Response(await getCaasInfo(candidateId));
        }
      }

      if (isTaskEnabledByAppConfig(BGC_CHECK_IN_SCREENING_TASK_ID, applicationId)) {
        setBgcResponse(await fetchWithTimeout(bgcURL));
      }

      if (isTaskEnabledByAppConfig(DT_IN_SCREENING_CHECK_TASK_ID, applicationId)) {
        setDtResponse(await fetchWithTimeout(dtURL));
      }

      if (
        isTaskEnabledByAppConfig(AIR_BGC_IN_SCREENING_CHECK_TASK_ID, applicationId) &&
        isAirJob(jcDetails?.businessLine as string)
      ) {
        setAirBgcResponse(await fetchWithTimeout(airBgcURL));
      }
    } catch (e) {
      setError(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    requestData();
  }, []);

  const property: ScreeningResultsControlledProperty = {
    dtResponse: dtResponse,
    bgcResponse: bgcResponse,
    airBgcResponse: airBgcResponse,
    i9Response: i9Response,
    loading: loading,
    error: error,
    stage: stage,
  };

  return <ScreeningResultsControlled property={property} />;
};

export function isAirJob(businessLine: string | undefined) {
  return businessLine === "AIR";
}
